
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';

export function WebhookEndpoints() {
  const [open, setOpen] = useState(false);
  const [editItem, setEditItem] = useState<any>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Form state
  const [url, setUrl] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [description, setDescription] = useState('');
  const [maxRetries, setMaxRetries] = useState(3);
  const [timeoutMs, setTimeoutMs] = useState(10000);
  const [isActive, setIsActive] = useState(true);
  const [isTestLoading, setIsTestLoading] = useState<number | null>(null);
  
  // Query webhook endpoints
  const { data: endpoints, isLoading } = useQuery({
    queryKey: ['webhook-endpoints'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('webhook_endpoints')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
  });
  
  // Create or update endpoint
  const mutation = useMutation({
    mutationFn: async (values: any) => {
      if (editItem) {
        // Update
        const { data, error } = await supabase
          .from('webhook_endpoints')
          .update({
            url: values.url,
            secret_key: values.secretKey,
            description: values.description,
            max_retries: values.maxRetries,
            timeout_ms: values.timeoutMs,
            is_active: values.isActive,
          })
          .eq('endpoint_id', editItem.endpoint_id)
          .select();
        
        if (error) throw error;
        return data;
      } else {
        // Create
        const { data, error } = await supabase
          .from('webhook_endpoints')
          .insert({
            url: values.url,
            secret_key: values.secretKey,
            description: values.description,
            max_retries: values.maxRetries,
            timeout_ms: values.timeoutMs,
            is_active: values.isActive,
          })
          .select();
        
        if (error) throw error;
        return data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhook-endpoints'] });
      toast({
        title: `Endpoint ${editItem ? 'Updated' : 'Created'}`,
        description: `Successfully ${editItem ? 'updated' : 'created'} the webhook endpoint.`,
      });
      resetForm();
      setOpen(false);
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'An error occurred',
        variant: 'destructive',
      });
    },
  });
  
  // Reset form
  const resetForm = () => {
    setUrl('');
    setSecretKey('');
    setDescription('');
    setMaxRetries(3);
    setTimeoutMs(10000);
    setIsActive(true);
    setEditItem(null);
  };
  
  // Open dialog for editing
  const handleEdit = (item: any) => {
    setEditItem(item);
    setUrl(item.url);
    setSecretKey(item.secret_key);
    setDescription(item.description || '');
    setMaxRetries(item.max_retries);
    setTimeoutMs(item.timeout_ms);
    setIsActive(item.is_active);
    setOpen(true);
  };
  
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate({
      url,
      secretKey,
      description,
      maxRetries,
      timeoutMs,
      isActive,
    });
  };
  
  // Test endpoint - Use Supabase Edge Function to proxy the request
  const testEndpoint = async (endpoint: any) => {
    const endpointId = endpoint.endpoint_id;
    setIsTestLoading(endpointId);
    
    try {
      // Using the edge function to bypass CORS issues
      const { data, error } = await supabase.functions.invoke('test-webhook-endpoint', {
        body: {
          url: endpoint.url,
          secretKey: endpoint.secret_key,
          payload: {
            test: true,
            timestamp: new Date().toISOString(),
          }
        }
      });
      
      if (error) throw error;
      
      if (data.success) {
        toast({
          title: 'Endpoint Test Successful',
          description: `Received status ${data.statusCode}`,
        });
      } else {
        toast({
          title: 'Endpoint Test Failed',
          description: data.message || `Received status ${data.statusCode || 'unknown'}`,
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('Error testing endpoint:', error);
      toast({
        title: 'Endpoint Test Failed',
        description: error.message || 'Connection failed',
        variant: 'destructive',
      });
    } finally {
      setIsTestLoading(null);
    }
  };
  
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Webhook Endpoints</CardTitle>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm}>Add Endpoint</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editItem ? 'Edit' : 'Add'} Webhook Endpoint</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="url">URL</Label>
                  <Input 
                    id="url" 
                    value={url} 
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://n8n.example.com/webhook/..."
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="secret-key">Secret Key</Label>
                  <Input 
                    id="secret-key" 
                    value={secretKey} 
                    onChange={(e) => setSecretKey(e.target.value)}
                    placeholder="Secret key for HMAC signing"
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea 
                    id="description" 
                    value={description} 
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Purpose of this webhook endpoint"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="max-retries">Max Retries</Label>
                    <Input 
                      id="max-retries" 
                      type="number"
                      value={maxRetries} 
                      onChange={(e) => setMaxRetries(parseInt(e.target.value))}
                      required
                      min="0"
                      max="10"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="timeout">Timeout (ms)</Label>
                    <Input 
                      id="timeout" 
                      type="number"
                      value={timeoutMs} 
                      onChange={(e) => setTimeoutMs(parseInt(e.target.value))}
                      required
                      min="1000"
                      max="60000"
                      step="1000"
                    />
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch 
                    id="is-active" 
                    checked={isActive}
                    onCheckedChange={setIsActive}
                  />
                  <Label htmlFor="is-active">Active</Label>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={mutation.isPending}>
                  {mutation.isPending ? 'Saving...' : 'Save'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </CardHeader>
      
      <CardContent>
        {isLoading ? (
          <div>Loading...</div>
        ) : (
          <ScrollArea className="h-[60vh] w-full">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>URL</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Max Retries</TableHead>
                  <TableHead>Timeout</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {endpoints?.map((item) => (
                  <TableRow key={item.endpoint_id}>
                    <TableCell className="font-medium max-w-xs truncate">
                      {item.url}
                    </TableCell>
                    <TableCell>{item.description}</TableCell>
                    <TableCell>
                      {item.is_active ? (
                        <span className="text-green-500">Active</span>
                      ) : (
                        <span className="text-gray-500">Inactive</span>
                      )}
                    </TableCell>
                    <TableCell>{item.max_retries}</TableCell>
                    <TableCell>{item.timeout_ms}ms</TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button variant="outline" size="sm" onClick={() => handleEdit(item)}>
                          Edit
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => testEndpoint(item)}
                          disabled={isTestLoading === item.endpoint_id}
                        >
                          {isTestLoading === item.endpoint_id ? 'Testing...' : 'Test'}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}
